import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  effect,
  ElementRef,
  EventEmitter,
  inject,
  OnInit,
  Output,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';

import { set } from 'date-fns';
import { firstValueFrom } from 'rxjs';
import { debounceTime } from 'rxjs';

import { Store } from '@ngrx/store';

import { selectIsLoadingAway, selectUser } from '../../../../store/app.selectors';
import { IUserProfile } from '../../../../modules/user/interfaces/IUserProfile';
import { AlertModalComponent } from '../../../shared/components/alert/alert.component';
import { setLoadingResolve } from '../../../shared/store/ui.actions';
import { cancelAway, sendAway } from '../../../user/store/user.actions';
import { IMessageableEntity } from '../../interfaces/IMessageableEntity';
import { MessagingApiService } from '../../services/messaging-api/messaging-api.service';
import { WebsocketService } from '../../services/websocket.service';
import { AppStateWithChat } from '../../store/chat.reducer';

function userValidator(control: AbstractControl): ValidationErrors | null {
  const value = control.value as { uid: string };
  if (value && typeof value === 'object' && typeof value.uid === 'string') {
    return null;
  }
  return { invalidUser: true };
}

function nonWhitespaceValidator(control: AbstractControl): ValidationErrors | null {
  if (!control?.value) {
    return null;
  }
  return !(control.value as string).trim().length ? { invalidMessage: true } : null;
}

@Component({
  selector: 'ngx-messaging-away',
  templateUrl: './ngx-messaging-away.component.html',
  styleUrls: ['./ngx-messaging-away.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class NgxMessagingAwayComponent implements OnInit {
  private destroyRef = inject(DestroyRef);
  private store = inject(Store<AppStateWithChat>);
  private dataService = inject(MessagingApiService);
  private fb = inject(FormBuilder);
  private dialog = inject(MatDialog);
  websocketService = inject(WebsocketService);

  @Output() closeEvent = new EventEmitter<boolean>();

  myTextarea = viewChild<ElementRef<HTMLTextAreaElement>>('myTextarea');

  user = toSignal(this.store.select(selectUser), { initialValue: null as IUserProfile | null });
  isLoadingAway = toSignal(this.store.select(selectIsLoadingAway), { initialValue: false });
  users = signal<IMessageableEntity[]>([]);
  loadingUsers = signal(false);
  pageUsers = signal(1);
  searchTermUsers = signal('');
  textareaHeight = signal('auto');

  awayForm: FormGroup = this.fb.group(
    {
      toDay: [null, [Validators.required]],
      toHour: [null, [Validators.required]],
      message: [null, [Validators.required, nonWhitespaceValidator]],
      type: [null, [Validators.required]],
      user: [null, [Validators.required, userValidator]],
    },
    { validators: this.validateDateTime }
  );

  constructor() {
    effect((onCleanup) => {
      const user = this.user();
      if (!user) {
        return;
      }

      let cancelled = false;
      onCleanup(() => {
        cancelled = true;
      });

      if (user.away) {
        this.awayForm.disable();
        this.awayForm.get('type')?.setValue(user.away.userId ? 'DELEGATION' : 'MESSAGE');
        this.awayForm.get('toDay')?.setValue(user.away.to);
        this.awayForm.get('toHour')?.setValue(user.away.to);
        this.awayForm.get('message')?.setValue(user.away.message);
        if (user.away.userId) {
          void this.getUserById(user.away.userId).then((oldUser) => {
            if (cancelled || !oldUser?.length) {
              return;
            }
            this.awayForm.get('user')?.setValue({
              businessReference: { entityUID: user.away!.userId },
              displayName: oldUser[0].displayName,
            });
          });
        }
      } else {
        this.awayForm.enable();
        this.awayForm.reset();
      }
    });
  }

  ngOnInit(): void {
    this.store.dispatch(setLoadingResolve({ value: false }));
    this.searchUsers();
    this.awayForm
      .get('user')!
      .valueChanges.pipe(debounceTime(500), takeUntilDestroyed(this.destroyRef))
      .subscribe((data: string) => {
        this.pageUsers.set(1);
        this.searchTermUsers.set(data);
        this.users.set([]);
        this.searchUsers();
      });
  }

  validateDateTime(group: FormGroup): { [key: string]: boolean } | null {
    const toDay = group.get('toDay')?.value as Date;
    const toHour = group.get('toHour')?.value as Date;
    if (!toDay || !toHour) {
      return null;
    }
    const to = set(toDay, {
      hours: toHour.getHours(),
      minutes: toHour.getMinutes(),
      seconds: toHour.getSeconds(),
      milliseconds: toHour.getMilliseconds(),
    });
    if (to < new Date()) {
      return { invalidDateTime: true };
    }
    return null;
  }

  updateValidators(type: string): void {
    const messageControl = this.awayForm.get('message');
    const userControl = this.awayForm.get('user');
    messageControl?.setValue(null);
    userControl?.setValue(null);
    if (type === 'DELEGATION') {
      messageControl?.clearValidators();
      userControl?.setValidators([Validators.required, userValidator]);
    } else if (type === 'MESSAGE') {
      userControl?.clearValidators();
      messageControl?.setValidators([Validators.required, nonWhitespaceValidator]);
    }
    messageControl?.updateValueAndValidity();
    userControl?.updateValueAndValidity();
  }

  save(): void {
    const toDay = this.awayForm.get('toDay')!.value as Date;
    const toHour = this.awayForm.get('toHour')!.value as Date;
    const to = set(toDay, {
      hours: toHour.getHours(),
      minutes: toHour.getMinutes(),
      seconds: toHour.getSeconds(),
      milliseconds: toHour.getMilliseconds(),
    });
    const message = this.awayForm.get('message')!.value as string;
    const userId = (this.awayForm.get('user')!.value as IMessageableEntity)?.businessReference?.entityUID;
    this.store.dispatch(sendAway({ to, message, userId }));
    this.closeEvent.emit(true);
  }

  cancel(): void {
    this.dialog
      .open(AlertModalComponent, {
        data: {
          title: 'Cancel away mode',
          message: 'Are you sure to cancel away mode?',
        },
      })
      .afterClosed()
      .subscribe((res: boolean) => {
        if (res) {
          this.store.dispatch(cancelAway());
          this.closeEvent.emit(true);
          this.awayForm.reset();
        }
      });
  }

  getOptionText(option: IMessageableEntity): string {
    return option?.displayName;
  }

  searchUsers(): void {
    this.loadingUsers.set(true);
    this.dataService
      .searchUsers({
        searchTerm: this.searchTermUsers(),
        page: this.pageUsers(),
        size: 500,
        status: 'ACTIVE',
      })
      .subscribe({
        next: (data) => {
          this.users.set(data.filter((res) => res.uid !== this.user()?.subjectMessageableEntity?.uid));
          this.loadingUsers.set(false);
        },
        error: () => {
          this.loadingUsers.set(false);
        },
      });
  }

  getUserById(id: string): Promise<IMessageableEntity[]> {
    return firstValueFrom(
      this.dataService.searchUsers({
        searchTerm: this.searchTermUsers(),
        page: this.pageUsers(),
        size: 20,
        entityUID: id,
      })
    );
  }

  autoResizeTextArea(): void {
    const textarea = this.myTextarea()?.nativeElement;
    if (!textarea) {
      return;
    }
    textarea.style.height = 'auto';
    textarea.style.height = textarea.scrollHeight + 'px';
    this.textareaHeight.set(textarea.style.height);
  }

  disablePastDates(date: Date | null): boolean {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date ? date >= today : false;
  }
}
